import { useState } from "react";
import { ChevronDown, Plus, X } from "lucide-react";
import { cn } from "../../lib/utils";

export interface ChipSelectProps<T extends string> {
  value: T;
  options: readonly T[];
  label: (v: T) => string;
  onChange: (v: T) => void;
  ariaLabel: string;
  onRemove?: () => void;
  removeLabel?: string;
  disabled?: boolean;
  tone?: "primary" | "muted";
}

/** A small changeable (and optionally removable) chip. Options open inline below it. */
export function ChipSelect<T extends string>({
  value,
  options,
  label,
  onChange,
  ariaLabel,
  onRemove,
  removeLabel,
  disabled,
  tone = "muted",
}: ChipSelectProps<T>) {
  const [open, setOpen] = useState(false);
  return (
    <span className="inline-flex flex-col gap-1.5">
      <span
        className={cn(
          "inline-flex items-center rounded-full border text-xs",
          tone === "primary"
            ? "border-primary/30 bg-primary/10 text-primary"
            : "border-border bg-background text-foreground/80"
        )}
      >
        <button
          type="button"
          disabled={disabled}
          aria-label={`${ariaLabel}: ${label(value)}`}
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          className="inline-flex items-center gap-1 ps-2.5 pe-2 py-1"
        >
          {label(value)}
          <ChevronDown className="w-3 h-3 opacity-60" />
        </button>
        {onRemove && (
          <button
            type="button"
            disabled={disabled}
            aria-label={removeLabel}
            onClick={onRemove}
            className="pe-2 ps-0.5 py-1 opacity-60 hover:opacity-100"
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </span>
      {open && (
        <span role="listbox" aria-label={ariaLabel} className="flex flex-wrap gap-1">
          {options.map((o) => (
            <button
              key={o}
              type="button"
              role="option"
              aria-selected={o === value}
              onClick={() => {
                onChange(o);
                setOpen(false);
              }}
              className={cn(
                "rounded-full border px-2 py-0.5 text-[11px]",
                o === value
                  ? "border-primary/40 bg-primary/10 text-primary"
                  : "border-border text-muted-foreground hover:border-primary/30"
              )}
            >
              {label(o)}
            </button>
          ))}
        </span>
      )}
    </span>
  );
}

export interface AddChipProps<T extends string> {
  text: string;
  options: readonly T[];
  label: (v: T) => string;
  onChange: (v: T) => void;
  ariaLabel: string;
  disabled?: boolean;
  /** Draw attention (e.g. a project that has no life area yet). */
  highlight?: boolean;
}

/** A dashed "+ …" chip for filing something the AI left unfiled. */
export function AddChip<T extends string>({ text, options, label, onChange, ariaLabel, disabled, highlight }: AddChipProps<T>) {
  const [open, setOpen] = useState(false);
  return (
    <span className="inline-flex flex-col gap-1.5">
      <button
        type="button"
        disabled={disabled}
        aria-label={ariaLabel}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "inline-flex items-center gap-1 rounded-full border border-dashed px-2.5 py-1 text-xs",
          highlight
            ? "border-primary/50 text-primary"
            : "border-border text-muted-foreground hover:border-primary/30 hover:text-foreground"
        )}
      >
        <Plus className="w-3 h-3" />
        {text}
      </button>
      {open && (
        <span role="listbox" aria-label={ariaLabel} className="flex flex-wrap gap-1">
          {options.map((o) => (
            <button
              key={o}
              type="button"
              role="option"
              aria-selected={false}
              onClick={() => {
                onChange(o);
                setOpen(false);
              }}
              className="rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground hover:border-primary/30"
            >
              {label(o)}
            </button>
          ))}
        </span>
      )}
    </span>
  );
}
